import React, { useEffect, useState, useContext } from 'react';
import {
  HashRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';
import groupBy from 'lodash/groupBy';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Button } from 'formlabs-web-components/Link';

import ProductDetailHeader from './ProductDetailHeader';
import CountryContext from '../../context/country';
import OptionSelector from './printer-pdp/OptionSelector';
import Amount from './Amount';
import { StoreMenu } from '../navigation';
import { analytics } from '../../utils';

import { getProductDetails } from './printer-pdp/productData';

import './ProductDetailPlugin.module.css';
import clientOnly from '../../hoc/clientOnly';

const PrinterDetailPlugin = props => {
  const [t] = useTranslation();
  const {
    attributes,
    baseUrl,
    category,
    media,
    menu,
    name,
    sku,
    bundleOptions,
    isWholesale = false,
    showAvailability = true
  } = props;
  const [selector, setSelector] = useState(false);
  const [amount, setAmount] = useState(1);
  const { path } = useContext(CountryContext);

  const toggleSelector = () => {
    setSelector(!selector);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  useEffect(
    () => {
      if (!selector) {
        handleOptionChange(7001028, new Map());
        handleOptionChange(7001019, new Map());
        handleOptionChange(7001029, new Map());
        handleOptionChange(7000973, new Map());
      }
    },
    [selector]
  );

  useEffect(() => {
    analytics.track('Product Viewed', {
      sku: sku,
      category: category,
      name: name,
      brand: 'Formlabs',
      quantity: 1
    });
  });

  const getOptionsSum = optionSums => {
    return Array.from(optionSums.values()).reduce(
      (sum, price) => sum + price,
      0
    );
  };
  const getOptionSum = selectedOptions => {
    return Array.from(selectedOptions.values()).reduce(
      (sum, option) => sum + option.price,
      0
    );
  };

  const getPrice = (optionsSum, amount) => {
    return (getBasePrice() + optionsSum) * amount;
  };
  const getBasePrice = () => {
    const { bundleOptions, price } = props;

    if (!bundleOptions) {
      return price;
    }

    return bundleOptions
      .filter(
        option =>
          option.product_links.length <= 1 &&
          (option.product_links.length === 1 && option.required === true)
      )
      .map(option => option.product_links)
      .reduce((sum, products) => {
        const price = products.reduce(
          (productSum, product) => productSum + product.price,
          0
        );
        return sum + price;
      }, 0);
  };
  const handleAmountChange = quantity => {
    setAmount(quantity);
  };
  useEffect(
    () => {
      updatePrice();
    },
    [amount]
  );

  let availableOptions = [];
  const defaultBundle = () => {
    const defaultOptions = new Map();
    if (bundleOptions) {
      const groupedOptions = groupBy(
        bundleOptions,
        option =>
          option.product_links.length > 1 ||
          (option.product_links.length === 1 && option.required === false)
      );

      // not configurable bundle options, we hide them
      if (groupedOptions['false']) {
        for (let entry of groupedOptions['false']) {
          // Handle the case where store-by-store product visibility makes some options have no items.
          if (entry.product_links.length !== 0) {
            defaultOptions.set(
              entry.option_id,
              new Map([[entry.product_links[0].sku, entry.product_links[0]]])
            );
          }
        }
      }

      // configurable bundle options
      if (groupedOptions['true']) {
        availableOptions = groupedOptions['true'];
      }
    }
    return defaultOptions;
  };
  const [bundle, setBundle] = useState(new Map(defaultBundle()));

  const initialOptions = () => {
    const intialValue = new Map();
    availableOptions.forEach(option => {
      const selectedOptions = new Map();
      const defaultItem = option.product_links.find(item => item.is_default);
      if (defaultItem) {
        selectedOptions.set(defaultItem.sku, defaultItem);
      }
      const optionSum = getOptionSum(selectedOptions);
      intialValue.set(option.option_id, optionSum);
    });
    return intialValue;
  };
  const [optionSums, setOptionSums] = useState(initialOptions());

  const handleOptionChange = (optionId, selectedOptions) => {
    const optionSum = getOptionSum(selectedOptions);
    const optionSumsLocal = optionSums.set(optionId, optionSum);
    setOptionSums(optionSumsLocal);
    bundle.set(optionId, selectedOptions);
    updatePrice();
  };
  const [price, setPrice] = useState(
    getPrice(getOptionsSum(optionSums), amount) || 0
  );
  const updatePrice = () => {
    setPrice(getPrice(getOptionsSum(optionSums), amount));
  };
  const flatOptionSkus = [];
  bundleOptions
    .map(option => {
      return option.product_links.map(product =>
        flatOptionSkus.push(product.sku)
      );
    })
    .concat();
  const productDetails = find(getProductDetails(t, bundleOptions, path), {
    sku
  });
  return (
    <>
      <StoreMenu baseUrl={baseUrl} menu={menu} isWholesale={isWholesale} />
      <Router>
        <Switch>
          {productDetails.steps.map((step, i) => {
            return (
              <Route path={`/step${i}/`} key={`product-step${i}`}>
                <OptionSelector
                  bundleOptions={bundleOptions}
                  toggleSelector={toggleSelector}
                  selector={selector}
                  price={price}
                  handleOptionChange={handleOptionChange}
                  amount={amount}
                  sku={sku}
                  name={name}
                  bundle={bundle}
                  baseUrl={baseUrl}
                  menu={menu}
                  step={i}
                  stepData={productDetails.steps}
                  productName={productDetails.productTitle}
                  toggleCallUs={productDetails.toggleCallUs}
                  experiment={productDetails.experiment}
                />
              </Route>
            );
          })}
          <Route path="/">
            <>
              {!selector && (
                <ProductDetailHeader
                  actions={
                    <div styleName="header-actions actions">
                      <div styleName="amount padded">
                        <Amount
                          onAmountChange={handleAmountChange}
                          defaultValue={amount}
                        />
                      </div>
                      <div style={{ alignSelf: 'center' }}>
                        <Button onClick={toggleSelector} to="/step0/">
                          {t('Buy now')}
                        </Button>
                      </div>
                    </div>
                  }
                  amount={amount}
                  attributes={attributes}
                  baseUrl={baseUrl}
                  bundleOptions={bundleOptions}
                  category={category}
                  media={media}
                  meta={
                    productDetails.additionalDescription ? (
                      <div styleName="link-section">
                        {productDetails.additionalDescription}
                      </div>
                    ) : null
                  }
                  name={name}
                  price={price}
                  sku={sku}
                  showAvailability={showAvailability}
                />
              )}
            </>
          </Route>
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
      </Router>
    </>
  );
};

export default compose(
  connect(state => {
    return {
      language: state.i18n.language,
      currency: state.i18n.currency
    };
  }),
  DisplayComponent,
  clientOnly
)(PrinterDetailPlugin);
